import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { invalidateBusinessInfoQuery } from '@/hooks/queries/useBusinessInfo'
import { useActiveBusiness } from '@/hooks/useCurrentActiveBusiness'
import { orpc } from '@/lib/orpc/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { updateBusinessSchema } from '@repo/shared/schemas/business'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import React from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { type z } from 'zod'

type BusinessSettingsForm = z.infer<typeof updateBusinessSchema>

export const Route = createFileRoute('/_dashboard/settings/business')({
  component: RouteComponent,
})

function RouteComponent() {
  const { activeBusiness } = useActiveBusiness()
  const updateBusinessMutation = useMutation(
    orpc.business.updateBusinessDetails.mutationOptions({
      onSettled: () => {
        invalidateBusinessInfoQuery()
      },
    }),
  )

  const form = useForm<BusinessSettingsForm>({
    resolver: zodResolver(updateBusinessSchema),
    defaultValues: {
      ...activeBusiness,
      blogUrl: activeBusiness?.blogUrl || '',
    },
  })

  const onSubmit = async (data: BusinessSettingsForm) => {
    if (!activeBusiness) {
      toast.error('No active business found')
      return
    }

    try {
      await updateBusinessMutation.mutateAsync({
        ...data,
        id: activeBusiness.id,
        businessName: data.businessName,
        businessDescription: data.businessDescription,
        targetAudience: data.targetAudience,
        tone: data.tone,
        products: data.products,
        pricing: data.pricing,
      })

      toast.success('Business details updated successfully!')
    } catch (error) {
      console.error('Error updating business:', error)
      toast.error('Failed to update business details')
    }
  }

  // Update form when activeBusiness changes
  React.useEffect(() => {
    if (activeBusiness) {
      form.reset({
        ...activeBusiness,
      })
    }
  }, [activeBusiness, form])

  if (!activeBusiness) {
    return (
      <div className="container px-8 py-8 mx-auto max-w-7xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            No Business Selected
          </h1>
          <p className="mt-2 text-gray-600">
            Please select a business to view settings.
          </p>
        </div>
      </div>
    )
  }
  return (
    <div className="container px-8 py-8 mx-auto max-w-7xl">
      <PageHeader
        title="Business Settings"
        description="Manage your business settings"
        className="mb-8"
      ></PageHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <input
            type="hidden"
            {...form.register('id')}
            value={Number(activeBusiness.id)}
          />
          <div>
            <Button
              type="submit"
              disabled={
                updateBusinessMutation.isPending || !form.formState.isDirty
              }
            >
              {updateBusinessMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Business Information */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Business Information</CardTitle>
                  <CardDescription>
                    Core details about your business
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="businessName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Business Name" />
                        </FormControl>
                        <FormDescription>
                          Your business name as it should appear in blog posts
                          and content.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="websiteUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Url</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Business Url" />
                        </FormControl>
                        <FormDescription>
                          Your business URL as it should appear in blog posts
                          and content.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="blogUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Blog Url</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            placeholder="Blog Url"
                          />
                        </FormControl>
                        <FormDescription>
                          Your blog URL as it should appear in blog posts and
                          content.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="businessDescription"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Description</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={3} maxLength={1000} />
                        </FormControl>
                        <FormDescription>
                          Describe your business mission, values, and what makes
                          you unique. This helps the AI create authentic content
                          that reflects your brand.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="targetAudience"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Target Audience</FormLabel>
                        <FormControl>
                          <Textarea maxLength={150} {...field} rows={2} />
                        </FormControl>
                        <FormDescription>
                          Who are your ideal customers? Include demographics,
                          pain points, and interests to help the AI write
                          content that resonates with your audience.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="other"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Other</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            maxLength={2000}
                            rows={2}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormDescription>
                          Other information about your business. This could be
                          anything you think the AI should know when writing
                          blog posts.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Pricing and Recent Posts */}
            <div className="space-y-4">
              {/* Articles */}
              <Card>
                <CardHeader>
                  <CardTitle>Articles</CardTitle>
                  <CardDescription>
                    Article settings that help guide content creation
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="tone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tone and Style</FormLabel>
                        <FormControl>
                          <Textarea maxLength={100} {...field} rows={2} />
                        </FormControl>
                        <FormDescription>
                          Describe the writing style and tone you prefer (e.g.,
                          professional, casual, technical, friendly). This
                          ensures all generated content matches your brand
                          voice.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="imageStyle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Image Style</FormLabel>
                        <FormControl>
                          <Textarea
                            maxLength={100}
                            {...field}
                            value={field.value || ''}
                            rows={2}
                          />
                        </FormControl>
                        <FormDescription>
                          Describe the image style you prefer for your articles.
                          This helps the AI generate images that match your
                          branding.. (e.g. "clean, well-composed image with
                          balanced lighting, clear focus, and harmonious colors.
                          The style should be polished and visually
                          appealing.").
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Pricing */}
              <Card>
                <CardHeader>
                  <CardTitle>Pricing</CardTitle>
                  <CardDescription>
                    Your pricing structure and packages
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="pricing"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pricing Tiers</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            maxLength={500}
                            rows={4}
                            placeholder="Starter: $99/month - Basic features&#10;Professional: $299/month - Advanced features + support&#10;Enterprise: Custom pricing - Full customization + dedicated team"
                          />
                        </FormControl>
                        <FormDescription>
                          Outline your pricing structure and packages. This
                          information helps create content that addresses
                          pricing concerns and highlights value propositions.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Products/Services */}
              <Card>
                <CardHeader>
                  <CardTitle>Products & Services</CardTitle>
                  <CardDescription>What your business offers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="products"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Primary Products/Services</FormLabel>
                        <FormControl>
                          <Textarea
                            maxLength={1000}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e.target.value.split('\n'))
                            }}
                            value={field.value?.join('\n') || ''}
                            rows={5}
                            placeholder="• Custom Software Development&#10;• Cloud Infrastructure Solutions&#10;• Digital Transformation Consulting&#10;• Technical Support & Maintenance&#10;• Training & Workshops"
                          />
                        </FormControl>
                        <FormDescription>
                          List your main products or services, one per line.
                          This helps the AI create relevant content that
                          showcases your offerings.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="keyFeatures"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Key Features</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            maxLength={2000}
                            onChange={(e) => {
                              field.onChange(e.target.value.split('\n'))
                            }}
                            value={field.value?.join('\n') || ''}
                            rows={5}
                            placeholder="• Custom Software Development&#10;• Cloud Infrastructure Solutions&#10;• Digital Transformation Consulting&#10;• Technical Support & Maintenance&#10;• Training & Workshops"
                          />
                        </FormControl>
                        <FormDescription>
                          List the key features of your products or services,
                          one per line. This helps the AI highlight important
                          aspects in the content it generates.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
